import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, forwardRef, Injector, Input, OnInit, QueryList, TemplateRef, ViewChild, AfterContentInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { MultiSelect, MultiSelectChangeEvent } from 'primeng/multiselect';
// Migrated 1:1 from @nexato/nx-core-module (formControls/edit-multiSelect-v2-form-control.component.ts, #683)
import { NxTemplateDirective } from '../../directives/nx-template.directive';

@Component({
    selector: 'nx-edit-multiselect-v2-input',
    template: `
    <div class="nx-edit-multi-select">
        <label *ngIf="showLabel" for="multiSelect">{{label | translate}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <p-multiSelect
            #multiSelect
            id="multiSelect"
            [optionLabel]="optionLabel"
            [dataKey]="dataKey"
            [optionValue]="optionValue"
            [maxSelectedLabels]="maxSelectedLabels"
            [placeholder]="placeholder | translate"
            [options]="options"
            [(ngModel)]="selected"
            (onChange)="onInputChange($event)"
            [appendTo]="'body'"
            [selectedItemsLabel]="('coreModule.editMultiSelectV2InputComponent.selectedItemsLabel' | translate:{ count: selected?.length || 0 })"
            [panelStyle]="panelStyle"
            [panelStyleClass]="'nx-edit-multi-select' + (panelWidhtFull ? ' full-width' : '') + (panelStyleClass ? ' ' + panelStyleClass : '')"
            [filter]="true">
            <ng-template let-value pTemplate="selectedItems" *ngIf="selectedItemsTemplate && selected">
                <ng-container *ngTemplateOutlet="selectedItemsTemplate; context: { $implicit: value }"></ng-container>
            </ng-template>
            <ng-template let-item pTemplate="item" *ngIf="itemTemplate">
                <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item }"></ng-container>
            </ng-template>
        </p-multiSelect>
        <small *ngIf="helpText">{{helpText | translate}}</small>
    </div>
  `,
    styles: [`

  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditMultiSelectV2InputComponent),
            multi: true
        }
    ]
})
export class EditMultiSelectV2InputComponent implements ControlValueAccessor, OnInit, AfterViewInit, AfterContentInit {

    /**
     * Reference to the underlying PrimeNG MultiSelect component
     */
    @ViewChild('multiSelect') multiSelect: MultiSelect;

    // native options
    /**
     * A property to define the label of an option in the options collection:
     * What will be shown to the user.
     */
    @Input() optionLabel: string | undefined;

    /**
     * A property to uniquely identify an option in the options collection:
     * Used to track selections.
     */
    @Input() dataKey: string | undefined;

    /**
     * A property to define the value of an option in the options collection:
     * What will be returned on selection.
     */
    @Input() optionValue: string | undefined;

    /**
     * Max number of labels shown when multiple items are selected.
     */
    @Input() maxSelectedLabels: number = 3;

    /**
     * Placeholder text to show when no item is selected.
     */
    @Input() placeholder: string = 'coreModule.editMultiSelectV2InputComponent.placeholder';

    /**
     * The options that should be shown in the dropdown
     */
    @Input() options: any[];

    // nx modul options
    /**
     * If true, the panel will have full width of the parent.
     */
    @Input() panelWidhtFull = false;

    /**
     * Inline styles to apply to the dropdown panel.
     */
    @Input() panelStyle: { [key: string]: any } = {};

    /**
     * Additional CSS class(es) to apply to the dropdown panel.
     */
    @Input() panelStyleClass: string = '';

    /**
     * Help text that will be shown below the input field.
     */
    @Input() helpText: string;

    /**
     * Label that will be shown above the input field.
     */
    @Input() label: string;

    /**
     * If true, the label will be shown above the input field.
     */
    @Input() showLabel = true;

    ngControl: NgControl;

    /**
     * The selected options
     */
    selected: any[];

    private onChange: (value: any[]) => void;
    private onTouched: () => void;

    @ContentChildren(NxTemplateDirective) templates!: QueryList<NxTemplateDirective>;
    public selectedItemsTemplate: TemplateRef<any> | undefined;
    public itemTemplate: TemplateRef<any> | undefined;

    constructor(
        private _injector: Injector,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngAfterViewInit(): void {
        this.multiSelect.updateModel(this.selected);
        this.cdr.detectChanges();
    }

    ngAfterContentInit() {
        this.templates.forEach((item) => {
            switch (item.getType()) {
                case 'selectedItems':
                    this.selectedItemsTemplate = item.template;
                    break;
                case 'item':
                    this.itemTemplate = item.template;
                    break;
            }
        });
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    writeValue(selected: any[]): void {
        this.selected = selected;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Optional: Implementieren Sie die Logik zum Deaktivieren des Steuerelements
    }

    onInputChange(event: MultiSelectChangeEvent): void {
        if (this.onChange) {
            this.onChange(this.selected);
        }
    }

    onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    /**
     * Opens the dropdown and focuses the filter input
     */
    openAndFocusFilter(): void {
        // Open the dropdown
        this.multiSelect.show();

        // Wait for the dropdown panel to render, then focus the filter input
        setTimeout(() => {
            // Find the filter input in the DOM (PrimeNG MultiSelect uses p-multiselect-filter class)
            const filterInput = document.querySelector('.p-multiselect-panel .p-multiselect-filter') as HTMLInputElement;
            if (filterInput) {
                filterInput.focus();
            }
        }, 50);
    }

}
